/*************
 * CURRENTLY:
 *
 * Currently Vegas charts are colored in 2 ways, on-the-fly and persistent:
 *   - when Vegas charts were first introduced, they were colored on-the-fly,
 *     i.e. a pure function of the encoding. This was easy to understand but
 *     not a great experience, because that meant that you might have 2 lines
 *     in your line chart, and then when you add a 3rd line, all 3 lines change
 *     in color. Similarly you might change the formula of one series, and the
 *     colors of the others change. This was especially bad with drilldown: on
 *     a bar graph with green, blue, and yellow bars, you could drill down on
 *     the blue bar and get green bars (even though the correct data was being
 *     displayed)
 *
 *   - to solve that problem, we introduced persistent coloring. When you add a
 *     3rd series, no matter where in the legend it is sorted to, the other 2
 *     series won't change in color. When you change the formula of a series,
 *     no colors change at all. This implemented with a plain JS object in the
 *     format blob, that maps series name -> color hex string, updated by
 *     updateMapFromSeriesToColor() on any change to the chart's encoding.
 *     One downside of this coloring strategy is that it's now path-dependent,
 *     i.e. the coloring depends on how you got to an encoding, not just what
 *     the encoding is that you ended up at. This feels less predictible and
 *     harder for the analyst to control. Therefore, we provide a way for the
 *     analyst to re-color the chart "from scratch" given the current encoding,
 *     by calling regenerateMapFromSeriesToColor(). Any two charts with the
 *     same encoding and the same palette will be colored identically by
 *     regenerateMapFromSeriesToColor(), which should feel much more predictible
 *     to the analyst
 *
 * So that explains what updateMapFromSeriesToColor() and regenerateMapFromSeriesToColor()
 * do, but what about backfillMapFromSeriesToColor()? That deals with the
 * transition from on-the-fly coloring to persistent coloring. If we just
 * did updateMapFromSeriesToColor() on an existing chart using an empty
 * color mapping, then once that got deployed, an analyst could wake up
 * the next morning and a chart they they haven't changed or re-ran the
 * query for, in a report they haven't touched at all, could have changed
 * colors. On the other hand, if we continued to color existing charts
 * on-the-fly and only used persistent coloring on new charts created
 * after deployment, on-the-fly colored charts could continue sticking
 * around for a long time, which have the bad behavior explained above,
 * plus it wouldn't be clear why some charts have one coloring behavior
 * and other charts have another coloring behavior.
 *
 * So what we do is, when rendering on-the-fly colored charts or when
 * loading one in the editor (they are identified by the absence of a
 * `format.color.map` plain JS object in the format blob (NOT an empty
 * map, but `undefined`)), we use backfillMapFromSeriesToColor() to
 * generate a color mapping such that the chart will look exactly how
 * it used to. That color mapping plain JS object is loaded into state
 * exactly as if it were loaded from the DB; if we are in the editor,
 * then when the chart is edited, that entire mapping (updated to
 * account for that edit) will be saved to the DB, "upgrading" that
 * chart to a persistently colored chart. Note that this is effectively
 * an on-the-fly backfill that, downstream, is equivalent to if, at the
 * time we deployed the change, we also backfilled the color mapping
 * of every existing chart with a color mapping generated such that the
 * chart will look exactly how it used to look. (It would be annoying
 * to actually implement such a backfill because our database migration
 * infrastructure is in Rake and based on Ruby, but generating the
 * mapping requires calling out to Vegas, a large JS codebase.)
 *
 * (Meanwhile, brand new charts all start with an empty map in
 * `format.color.map`, starting from when this change was deployed.)
 *
 *
 *
 **********
 * FUTURE:
 *
 * Forthcoming shortly, there will be another, 3rd way to color charts.
 * The problem with persistent coloring is that it is individual to
 * each chart. Different charts may use totally different charts for
 * the same series, and worse, use the same colors for different series.
 * so the new, 3rd way is report-level persistent coloring: a plain JS
 * object of nested maps stored and persisted on the report, that maps
 * color palette token concatenated with the formula of the color field
 * pill -> a map from series name -> color hex string.
 *
 * The transition story must once again balance actively pushing charts
 * to become report-level colored (hence, not just only using report-
 * level coloring on new charts), but also avoid surprising changes to
 * the colorings of existing charts (so *definitely* not changing the
 * colorings of charts in reports that aren't touched, and also nothing
 * where an analyst adds a series to a chart and all the colors of all
 * series change in response (to become consistent with other charts in
 * the report, say)).
 *
 * Unfortunately we can't use the same transition strategy again,
 * because turning a chart-level persistently colored chart into a
 * report-level persistently colored chart actually *loses* information.
 * (We wouldn't lose information if we transitioned to the future-FUTURE
 * chart coloring where there may be chart-level overrides of report-
 * level colors, but if all current chart-level colors become chart-
 * level overrides, that's equivalent to only using report-level
 * coloring on new charts and wouldn't be actively pushing existing
 * charts to become report-level colored.)
 *
 * So instead, where a palette + color field pill pair is unique, we'll
 * automatically promote that coloring to the report-level (easy case);
 * otherwise, we'll only switch a chart's coloring from chart-level
 * to report-level when:
 *   - the analyst changes the color pill field or the palette
 *   - re-assigns the current palette to another chart with the same
 *     palette + color field pill pair
 *   - the analyst creates a new chart with the same palette + color
 *     field pill pair, and this chart was the last one edited, then
 *     it'll be "promoted" as well.
 *
 * See also: https://mode.slack.com/archives/CB7BGMGUE/p1549507289180700
 *
 */

import { Attr, Names } from '@mode-switch/model';
import { DomainResult, SelectPlan } from '@mode-switch/query';
import { EncField, Encoding } from '@mode-vegas/vegas';

import {
  ChartEncoding,
  computePalette,
  EncodingHelpers,
  makeSeriesNameFormatter,
  SchemaModel,
} from '@mode/charts/util-vegas';
import { ReportResource } from '@mode/shared/util-halogen';
import { ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import * as angular from 'angular';

function colorFieldFormula(encoding: Encoding): string | undefined {
  let field: EncField;
  switch (ChartEncoding.shape(encoding)) {
    // special case for Line+Bar (aka Combo) charts
    case ChartEncoding.Shape.Combo:
      return 'LINE+BAR';
    case ChartEncoding.Shape.Stacked:
    case ChartEncoding.Shape.Grouped:
      field = encoding.marks.series[0] && encoding.marks.series[0].properties.color.values[0];
      break;
    case ChartEncoding.Shape.Radial:
      field = encoding.marks.properties.color.values[0];
      break;
  }
  if (field) {
    return field.formula;
  }
}

function colorScopeFromFormula(formula: string, colorPaletteToken: string, queryToken: string): string {
  let scope = `${colorPaletteToken}_${formula}`;
  if (formula === Names.Key) {
    scope += `_${queryToken}`;
  }
  return scope;
}

export function colorScope(encoding: Encoding, colorPaletteToken: string, queryToken: string): string | undefined {
  const formula = colorFieldFormula(encoding);
  if (formula) {
    return colorScopeFromFormula(formula, colorPaletteToken, queryToken);
  }
}

/**
 * For the backfill context, get the colorField from encoding.
 * This is shared between flamingo and non-flamingo contexts.
 */
export function backfillGetColorField(encoding: Encoding) {
  // get color field
  let colorField: EncField | EncField[] | undefined;
  if (encoding.marks.series.length) {
    const [series] = encoding.marks.series;
    if (series.properties.color.values.length) {
      // line, bar, area, & scatter charts
      colorField = series.properties.color.values[0];
    } else {
      const [y] = encoding.y,
        [y2] = encoding.y2;
      if (y && y2) {
        // special case for Line+Bar (aka Combo) charts
        colorField = [y, y2];
      }
    }
  } else if (encoding.marks.properties.color.values) {
    // pie & donut charts
    colorField = encoding.marks.properties.color.values[0];
  }

  return colorField;
}

export function flamingoBackfillGetSeriesNames(
  colorField: EncField | EncField[],
  format: ChartTypes.ChartFormat,
  model: SchemaModel,
  encoding: Encoding,
  plan: FlamingoTypes.ModeSelectPlan,
  planResult: FlamingoTypes.ModeSelectResult
): string[] {
  let seriesNames: string[] = [];
  if (colorField instanceof Array) {
    // special case for Line+Bar (aka Combo) charts
    seriesNames = colorField.map((encField) => {
      const field = model.makeField(encField);
      return field.name;
    });
  } else if (colorField.formula === Names.Key) {
    seriesNames = encoding.value
      .map((encField) => {
        const field = model.makeField(encField);
        return field.name;
      })
      .sort();
  } else {
    const field = model.makeField(colorField);
    const formatter = makeSeriesNameFormatter(model.compileField(field), format);

    // If we get series names from the domain query run by flamingo. If we get to this point, we should
    // always expect there to be:
    // 1. At least 2 queries
    // 2. Optionally, 1 color domain plan
    // eslint-disable-next-line no-eq-null, eqeqeq
    if (plan != null && SelectPlan.isPivot(plan) && planResult.results.length > 1) {
      const domainResult = DomainResult.get(plan, planResult, field.alias);
      if (domainResult?.type === Attr.Type.Discrete) {
        const domainValues = domainResult.values.slice().sort();
        seriesNames = domainValues.map(formatter);
      }
    }
  }

  return seriesNames;
}

/**
 * The on the fly backfills
 *
 * There are two functions here, they share all of the same logic except for how they
 * generate a color domain as the vegas model does not actually have data on it in the
 * flamingo context. In that context, we use a color domain query that has been added
 * to the flamingo plan.
 */
export const THERE_WOULD_BE_A_MAP_BUT_FOR_INVALID_ENCODING = {};
export function flamingoBackfillOnTheFlyColoring(
  format: ChartTypes.ChartFormat,
  model: SchemaModel,
  encoding: Encoding,
  plan: FlamingoTypes.ModeSelectPlan,
  planResult: FlamingoTypes.ModeSelectResult
): {
  [seriesName: string]: string;
} {
  // Do validation
  const existingMap = format.colors.map;
  if (existingMap && existingMap !== THERE_WOULD_BE_A_MAP_BUT_FOR_INVALID_ENCODING) {
    return existingMap;
  }

  if (EncodingHelpers.validateChartEncodingFields(encoding, model).length > 0) {
    return THERE_WOULD_BE_A_MAP_BUT_FOR_INVALID_ENCODING;
  }

  // Get color field
  const colorField = backfillGetColorField(encoding);
  // else incomplete encoding
  if (!colorField) {
    return format.colors.map || {};
  }

  // Get series names
  const seriesNames = flamingoBackfillGetSeriesNames(colorField, format, model, encoding, plan, planResult);

  // Build map
  const map: {
    [seriesName: string]: string;
  } = {};
  const palette = computePalette(format);
  seriesNames.forEach((seriesName, i) => {
    map[seriesName] = palette[i % palette.length];
  });

  return map;
}

export function updateFirstPassFlamingoChartColoring(
  oldColoring: { [seriesName: string]: string },
  format: ChartTypes.ChartFormat,
  oldEncoding: Encoding,
  newEncoding: Encoding,
  seriesNames: string[] | undefined
): { [seriesName: string]: string } {
  if (!seriesNames) {
    return oldColoring;
  }

  const map = {};
  const colors = computePalette(format);
  seriesNames.forEach((seriesName, i) => {
    map[seriesName] = colors[i % colors.length];
  });
  return map;
}

export function updateReportLevelColoring(
  oldColoring: ReportResource['color_mappings'],
  chart: ChartTypes.EncodedChart,
  newEncoding: Encoding,
  newSeriesNames: string[],
  oldSeriesNames?: string[],
  pillAction?: ChartTypes.PillActionType
): ReportResource['color_mappings'] {
  // Update the report-level chart coloring map
  //
  // *** INCOMPLETE ***
  //
  // - doesn't handle changing pill formula correctly (changes color)
  // - doesn't clean up mapping when Y-Axis pills are removed (that will
  //   require getting the lists of series names for every chart in
  //   the report)
  //
  // Note that it takes in the seriesNames as an array, rather than
  // expecting to be able to synchronously query Vegas. Currently the
  // reducer synchronously calls Vegas and passes in the retrieved
  // series names, but hopefully this can be easily migrated to
  // having an earlier side-effect asynchronously call Flamingo, and
  // then the reducer retrieves the series names via a memoized
  // selector.

  const paletteSize = computePalette(chart.format).length;
  const oldColorFormula = colorFieldFormula(chart.encoding);
  const newColorFormula = colorFieldFormula(newEncoding);

  const scope = colorScopeFromFormula(newColorFormula, chart.colorPaletteToken, chart.queryToken);
  const map = angular.copy(oldColoring[scope]) || {};

  // histogram of how many times each color in palette has already been assigned
  const histogram: { [color: number]: number } = {};
  for (const color of Object.values(map)) {
    histogram[color] = (histogram[color] || 0) + 1;
  }

  for (const seriesName of newSeriesNames) {
    if (map.hasOwnProperty('$' + seriesName)) {
      continue;
    }

    // get first color that's been used the least number of times
    // normally, this'll be the first unused color
    let min = Infinity,
      minColor: number;
    for (let color = 0; color < paletteSize; color += 1) {
      if (histogram[color] === undefined) {
        minColor = color;
        break;
      }
      if (histogram[color] < min) {
        min = histogram[color];
        minColor = color;
      }
    }
    histogram[minColor] = (histogram[minColor] || 0) + 1;

    map['$' + seriesName] = minColor; // why prepend the '$'? Because JS objects
    // can't quite be used as hashmaps with arbitrary string keys, '__proto__'
    // is special and 'constructor' is also an "own" property even on empty
    // objects. In fact, Google Docs used freeze if you wrote __proto__ in an
    // empty Google Doc http://2ality.com/2012/01/objects-as-maps.html
  }

  return {
    ...oldColoring,
    [scope]: map,
  };
}

export function updateChartLevelColoring(
  format: ChartTypes.ChartFormat,
  newSeriesNames: string[],
  encoding: Encoding
): { [seriesName: string]: string } {
  // Update the series-to-color mapping according to 4 rules:
  //   - when the color field changes, re-assign colors from scratch
  //   - if there's a field in the color channel, then persist colors assigned
  //     to all values in the domain of the field whether or not currently
  //     drawn, and assign least-used colors to values being drawn that don't
  //     have colors yet
  //   - if Measure Names is in the color channel, then only persist colors
  //     assigned to currently drawn series, and unassign colors from series
  //     that have been removed
  //   - except if a pill isn't actually removed, just its formula is changed,
  //     ensure its color doesn't change
  //
  // The rules for Measure Names vs non-Measure Names color fields are opposing
  // because they are built in opposite ways. Measure Names charts are built up
  // one series at a time by dragging fields one at a time into the Y-Axis card,
  // so it's nice to assign and unassign as you go. By contrast, color charts
  // are built by dragging a field into the Color card, which usually draws
  // every series all at once. Series may then be removed by drilling into a
  // series or by manually creating a filter, but when adding series you're
  // usually adding *back* a series that you had already seen when every series
  // was drawn.
  // (If you did start with a Measure Names chart with a bunch of series and
  //  drilled down, and then tried to add back a series, if the original series
  //  wasn't the default aggregation formula, then when you first dragged it
  //  back it would start with a different formula and get a different color
  //  anyway.)

  const map = { ...(format.colors.map ?? {}) };
  const colorFormula = colorFieldFormula(encoding);

  const setMinus = (A: string[], B: string[]) => A.filter((a) => !B.some((b) => b === a));

  // if a pill has been removed (other than just its formula was changed), unassign
  // its color, regardless of whether drilling down or pill was dragged away.
  // (if series aren't pills but are instead values in the domain of a color field,
  //  do NOT unassign colors from values that are filtered out)
  if (colorFormula === Names.Key) {
    const oldSeriesNames = Object.keys(map);
    for (const removedSeriesName of setMinus(oldSeriesNames, newSeriesNames)) {
      delete map[removedSeriesName];
    }
  }

  // histogram of how many times each color in palette has already been assigned
  const histogram: { [color: string]: number } = {};
  for (const seriesName of Object.keys(map)) {
    const color = map[seriesName];
    histogram[color] = (histogram[color] || 0) + 1;
  }

  let mapSize = Object.keys(map).length;
  const palette = computePalette(format);
  for (const seriesName of newSeriesNames) {
    // Skip if item is already in the map
    if (map[seriesName]) {
      continue;
    }
    // Finish if the map size is at the data limit
    if (mapSize >= ChartTypes.COLOR_DOMAIN_DATA_LIMIT) {
      break;
    }

    // get first color that's been used the least number of times
    // normally, this'll be the first unused color
    let min = Infinity,
      minColor: string;
    for (const color of palette) {
      if (histogram[color] === undefined) {
        minColor = color;
        break;
      }
      if (histogram[color] < min) {
        min = histogram[color];
        minColor = color;
      }
    }
    histogram[minColor] = (histogram[minColor] || 0) + 1;

    map[seriesName] = minColor;
    mapSize += 1;
  }

  return map;
}
